<template>
<div id="artists-playlist" class="artists">
    <div class="m-t-nav">
        <div class="big-m-t">
            <Playlist
            :playlist-position="0" 
            :playlist="profilesWorld"
            :listType="'profiles'"
            :editButtons="false"
            :title="false"
            :subTitle="subTitle"
            :key="reloadComponent"
            :view="'list'"
            />
        </div>
    </div>
</div>
</template>

<script>
import { rtdb } from '@/db.js'
import { mapGetters } from "vuex"
import { sanitizeKey } from '@/utils/helpers.js'
import Playlist from '@/components/playlists/Playlist.vue'

export default {
  name: 'ArtistsPlaylist',
  components: {
    Playlist
  },
  metaInfo: {
    title: "Playlist",
  },
  data() {
	  return {
        profilesWorld: {
          users: null,
        },
        subTitle: null,
        reloadComponent: false,
        locationPrefixCache: {} // Cache to avoid repeated database queries
	  }
  },
  created () {
    // fetch the data when the view is created and the data is
    // already being observed
    this.getProfilesWorld()
  },
  watch: {
    // call again the method if the route changes
    '$route': 'getProfilesWorld'
    // '$route' (to, from) {
    //   if(to !== from ) {
    //     this.getProfilesWorld();
    //   }
    // }
  },
  computed: {
    ...mapGetters([
      'getUser',
      'getUserSettings',
    ])
  },
  methods:{
    async getProfilesWorld() {
      if (this.$route.params.genreMatch) {
        const genreName = this.$route.params.genreMatch
        await this.getProfilesByGenre(genreName)
        return
      }

      const urlId = this.$route.params.id
      if (urlId && urlId.toLowerCase() === 'unranked') {
        this.getUnrankedProfiles()
        return
      }
      // Use the URL param directly (e.g., "NY" not "New York")
      const locationName = urlId
      
      console.log(`🔍 Finding rank data for location: ${locationName}`)
      
      // Try to find which location level has data for this location
      const rankPrefix = await this.findRankPrefix(locationName)
      
      if (!rankPrefix) {
        console.warn(`⚠️ No rank data found for location: ${locationName}`)
        this.profilesWorld.users = []
        this.subTitle = '0 artists'
        return
      }

      const sanitizedLocation = sanitizeKey(locationName)
      const rankPath = `ranks/${rankPrefix}${sanitizedLocation}`

      console.log(`✅ Using rank path: ${rankPath}`)

      // Query all ranked profiles for this location (not limited to top 10)
      rtdb.ref('rankProfiles')
        .orderByChild(rankPath)
        .startAt(1) // Only get profiles that have a rank (rank >= 1)
        .once('value', (snapshot) => {
          const results = []
          
          snapshot.forEach((child) => {
            const val = child.val()
            
            // Format data for Playlist component
            results.push({
              key: child.key,
              userSettings: val.userSettings,
              rankTotal: val.rankTotal,
              categoryRank: val.ranks?.[`${rankPrefix}${sanitizedLocation}`]
            })
          })

          console.log(`✅ Found ${results.length} ${locationName} artists`)
          
          this.profilesWorld.users = results
          this.subTitle = `${results.length} artists`
          this.reloadComponent = !this.reloadComponent
        })
        .catch(error => {
          console.error(`❌ Error fetching ${locationName} profiles:`, error)
        })
    },

    async getProfilesByGenre(genreName) {
      const sanitizedGenre = sanitizeKey(genreName)
      const rankPath = `ranks/${sanitizedGenre}`

      console.log(`🔍 Querying rankProfiles for genre ${genreName}: orderByChild("${rankPath}")`)

      rtdb.ref('rankProfiles')
        .orderByChild(rankPath)
        .startAt(1)
        .once('value', (snapshot) => {
          const results = []
          
          snapshot.forEach((child) => {
            const val = child.val()
            
            results.push({
              key: child.key,
              userSettings: val.userSettings,
              rankTotal: val.rankTotal,
              categoryRank: val.ranks?.[sanitizedGenre]
            })
          })

          console.log(`✅ Found ${results.length} ${genreName} artists`)
          
          this.profilesWorld.users = results
          this.subTitle = `${results.length} artists`
          this.reloadComponent = !this.reloadComponent
        })
        .catch(error => {
          console.error(`❌ Error fetching ${genreName} profiles:`, error)
        })
    },

    getUnrankedProfiles() {
      rtdb.ref('rankProfiles')
        .orderByChild('ratio')
        .equalTo(0)
        .once('value', (snapshot) => {
          const results = []
          snapshot.forEach((child) => {
            const val = child.val()
            results.push({
              key: child.key,
              userSettings: val.userSettings,
              rankTotal: val.rankTotal,
              categoryRank: null
            })
          })
          this.profilesWorld.users = results
          this.subTitle = `${results.length} artists`
          this.reloadComponent = !this.reloadComponent
        })
        .catch(error => {
          console.error('❌ Error fetching Unranked profiles:', error)
        })
    },

    /**
     * Dynamically finds which location level (loc0_, loc1_, loc2_, loc3_) has data
     * Uses caching to avoid repeated database queries
     */
    async findRankPrefix(locationName) {
      const sanitizedLocation = sanitizeKey(locationName)
      
      // Check cache first
      const cacheKey = sanitizedLocation
      if (this.locationPrefixCache[cacheKey]) {
        console.log(`📦 Using cached prefix for ${locationName}`)
        return this.locationPrefixCache[cacheKey]
      }
      
      const prefixes = ['loc0_', 'loc1_', 'loc2_', 'loc3_']
      
      // Special case: "world" always uses loc0_
      if (locationName.toLowerCase() === 'world') {
        this.locationPrefixCache[cacheKey] = 'loc0_'
        return 'loc0_'
      }

      // Try each prefix and see which one has data
      for (const prefix of prefixes) {
        const rankPath = `ranks/${prefix}${sanitizedLocation}`
        
        try {
          const snapshot = await rtdb.ref('rankProfiles')
            .orderByChild(rankPath)
            .startAt(1)
            .limitToFirst(1)
            .once('value')
          
          // If we found at least one result, this is the correct prefix
          if (snapshot.exists() && snapshot.numChildren() > 0) {
            console.log(`✅ Found data with prefix: ${prefix}`)
            // Cache the result
            this.locationPrefixCache[cacheKey] = prefix
            return prefix
          }
        } catch (error) {
          console.error(`Error checking prefix ${prefix}:`, error)
        }
      }
      
      // No data found with any prefix
      return null
    },
  }
  
}
</script>
<style lang="scss" scoped>
</style>